<template>
  <el-drawer
    v-model="taskStore.panelOpen"
    title="Word 题目处理"
    size="min(1180px, 96vw)"
    append-to-body
    class="qgen-task-drawer"
    @open="handleOpen"
    @closed="taskStore.closePanel"
  >
    <el-empty v-if="!taskStore.taskId" description="暂无处理任务" />
    <div v-else class="qgen-panel">
      <header class="qgen-summary">
        <div>
          <div class="qgen-title">
            <strong>{{ taskStore.inputFilename || 'Word 题目处理任务' }}</strong>
            <el-tag size="small" :type="statusTagType">{{ statusText }}</el-tag>
          </div>
          <p>{{ taskStore.message || '状态已与服务器同步' }}</p>
        </div>
        <div class="qgen-header-actions">
          <el-tooltip content="刷新">
            <el-button circle :icon="Refresh" :loading="taskStore.syncing" @click="taskStore.pollTask({ immediate: true })" />
          </el-tooltip>
          <el-tooltip v-if="taskStore.isRunning" content="结束任务">
            <el-button circle type="warning" :icon="CloseBold" @click="confirmCancel" />
          </el-tooltip>
          <el-tooltip v-if="taskStore.isTerminal" content="清除本地任务入口">
            <el-button circle :icon="Delete" @click="taskStore.clearTask" />
          </el-tooltip>
        </div>
      </header>

      <QuestionGenProgress :task="taskStore.task || {}" />

      <el-alert v-if="taskStore.networkError" type="warning" show-icon :closable="false" :title="taskStore.networkError" />
      <el-alert
        v-if="taskStore.task?.error_summary"
        type="error"
        show-icon
        :closable="false"
        :title="taskStore.task.error_summary"
      />

      <div class="qgen-review-toolbar">
        <QuestionGenReviewFilters
          :model-value="taskStore.filters"
          @change="taskStore.setFilters"
        />
        <div class="qgen-bulk-actions">
          <el-button :disabled="!pageIds.length || readonly" @click="taskStore.bulkAction('select', pageIds)">全选本页</el-button>
          <el-button :disabled="!pageIds.length || readonly" @click="taskStore.bulkAction('unselect', pageIds)">取消本页</el-button>
          <el-button type="success" plain :disabled="!selectedIds.length || readonly" @click="confirmSelected">确认所选</el-button>
          <el-button type="primary" plain :disabled="!selectedIds.length || readonly" @click="taskStore.retryDrafts('reviewing', selectedIds)">重新复核</el-button>
        </div>
      </div>

      <div class="qgen-gate-summary">
        <span>本页选中 <strong>{{ taskStore.selectedDraftCount }}</strong></span>
        <span>可入库 <strong class="is-success">{{ taskStore.commitEligibleCount }}</strong></span>
        <span>受阻 <strong class="is-danger">{{ taskStore.blockedSelectedCount }}</strong></span>
        <span class="qgen-gate-reason">AI 补全答案即使复核一致，也需人工确认后才能入库。</span>
      </div>

      <QuestionGenDraftReviewTable
        :drafts="taskStore.drafts"
        :loading="taskStore.draftsLoading"
        :readonly="readonly"
        :page="taskStore.page"
        :page-size="taskStore.pageSize"
        :total="taskStore.total"
        :categories="categories"
        @patch="taskStore.patchDraft"
        @bulk="taskStore.bulkAction"
        @retry="taskStore.retryDrafts"
        @answer-input="taskStore.setDraftAnswer"
        @confirm="taskStore.confirmDraft"
        @page-change="taskStore.setPage"
        @size-change="taskStore.setPageSize"
      />

      <footer class="qgen-footer">
        <el-button :disabled="!taskStore.total" :icon="Download" @click="taskStore.downloadExcel">下载审核稿</el-button>
        <el-button
          type="primary"
          :disabled="readonly || !taskStore.commitEligibleCount || taskStore.blockedSelectedCount > 0"
          @click="confirmCommit"
        >
          确认本页所选入库
        </el-button>
      </footer>
    </div>
  </el-drawer>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { CloseBold, Delete, Download, Refresh } from '@element-plus/icons-vue'
import { useQuestionGenTaskStore } from '@/store'
import api from '@/services/api'
import QuestionGenProgress from '@/components/question-gen/QuestionGenProgress.vue'
import QuestionGenReviewFilters from '@/components/question-gen/QuestionGenReviewFilters.vue'
import QuestionGenDraftReviewTable from '@/components/question-gen/QuestionGenDraftReviewTable.vue'

const taskStore = useQuestionGenTaskStore()
const categories = ref([])
const pageIds = computed(() => taskStore.drafts.map((item) => item.draft_id))
const selectedIds = computed(() => taskStore.selectedDraftIds)
const selectedDrafts = computed(() => taskStore.drafts.filter((item) => item.selected !== false))
const readonly = computed(() => taskStore.readonlyDrafts || !taskStore.canEditDrafts || taskStore.committed)

const flattenCategories = (items, level = 0) => (items || []).flatMap((category) => [
  {
    ...category,
    display_name: `${'　'.repeat(level)}${category.name}`
  },
  ...flattenCategories(category.children, level + 1)
])

const loadCategories = async () => {
  try {
    const response = await api.get('/question-categories')
    categories.value = flattenCategories(response.data?.categories || [])
  } catch (error) {
    ElMessage.error('加载题目分类失败: ' + (error.response?.data?.message || error.message))
  }
}

const statusText = computed(() => ({
  queued: '排队中', preflighting: '预审中', splitting: '切分中', normalizing: '规范中',
  answering: '补答案中', reviewing: '复核中', validating: '校验中',
  ready_for_manual_review: '待人工确认', committing: '入库中', succeeded: '已完成',
  partially_succeeded: '部分完成', failed: '失败', cancelling: '正在结束', cancelled: '已结束'
}[taskStore.status] || '处理中'))

const statusTagType = computed(() => ({
  ready_for_manual_review: 'warning', succeeded: 'success', partially_succeeded: 'warning',
  failed: 'danger', cancelling: 'warning', cancelled: 'info', queued: 'info'
}[taskStore.status] || ''))

const handleOpen = () => {
  taskStore.hydrate()
  taskStore.pollTask({ immediate: true })
  loadCategories()
}

const confirmCancel = async () => {
  try {
    await ElMessageBox.confirm(
      '结束后不会再发起新的模型请求；当前请求完成后停止，已生成题目会保留。',
      '结束本次处理？',
      { confirmButtonText: '结束任务', cancelButtonText: '继续处理', type: 'warning' }
    )
    await taskStore.cancelTask()
  } catch (error) {
    // User cancelled the dialog.
  }
}

const confirmSelected = async () => {
  await taskStore.confirmDrafts(selectedDrafts.value)
}

const confirmCommit = async () => {
  if (taskStore.blockedSelectedCount) {
    ElMessage.warning('所选题目仍有未确认答案或结构错误')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确认将本页 ${taskStore.commitEligibleCount} 道题目写入题库吗？`,
      '确认入库',
      { confirmButtonText: '确认入库', cancelButtonText: '继续审核', type: 'warning' }
    )
    await taskStore.commitDrafts()
  } catch (error) {
    // User cancelled the dialog.
  }
}
</script>

<style scoped>
.qgen-panel { display: flex; flex-direction: column; gap: 14px; }
.qgen-summary { display: flex; justify-content: space-between; gap: 16px; }
.qgen-title { display: flex; align-items: center; gap: 8px; color: #303133; }
.qgen-summary p { margin: 5px 0 0; color: #606266; font-size: 13px; }
.qgen-header-actions, .qgen-bulk-actions, .qgen-footer { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.qgen-review-toolbar { display: flex; justify-content: space-between; gap: 12px; align-items: center; flex-wrap: wrap; }
.qgen-bulk-actions { justify-content: flex-end; }
.qgen-gate-summary { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; color: #606266; font-size: 13px; }
.qgen-gate-summary strong { color: #303133; }
.qgen-gate-summary .is-success { color: #529b2e; }
.qgen-gate-summary .is-danger { color: #f56c6c; }
.qgen-gate-reason { color: #909399; }
.qgen-footer { justify-content: flex-end; padding-top: 2px; }
@media (max-width: 760px) { .qgen-review-toolbar { align-items: stretch; } }
</style>
